import { arcContents } from "./ARC.contents";

export function arcRatingItems(ratingItem: any, ratingGroup?: any): Array<any> {
    const groupId = (ratingGroup || {}).programEnrollmentRatingGroupId || "";
    const itemId = ratingItem.semanticItemId || (ratingItem.programEnrollmentRatingItemId || "").toString();
    let parentItemId = ratingItem.semanticParentItemId || ratingItem.parentItemId;
    const contentItems = [];

    // [ATA]
    if (ratingGroup && [17, 23, 11].indexOf(groupId) > -1) { return [ataDropdown(ratingGroup)]; }
    if (ratingGroup && [13, 14].indexOf(groupId) > -1) { return [ataAggregate(ratingGroup)]; }
    if (ratingGroup && itemId === "51" && ratingGroup.programEnrollmentRatingGroupContents) {
        ratingGroup.programEnrollmentRatingGroupContents.forEach(contentItem => {
            contentItems.push({
                type: "none",
                label: contentItem.content
            });
        });
    }

    // Create form item.
    const item: any = {
        itemId,
        parentItemId: parentItemId ? parentItemId.toString() : (ratingGroup && !ratingItem.sectionDisplaySequence) ? "registerForm" : "",
        type: getControlType(ratingItem),
        label: ratingItem.display || ratingItem.ratingItemDescription || ratingItem.ratingGroupDescription,
        className: ratingItem.className || "",
        hint: ratingItem.hint,
        placeholder: ratingItem.placeHolder,
        required: ratingItem.required,
        mask: ratingItem.mask,
        data: ratingItem,
        visible: true
    };

    // Require all inputs.
    if (["radioGroup", "text", "number", "date", "attachment", "table", "textarea"].indexOf(item.type) > -1) { item.required = true; }

    // Fix conditional group label.
    if (item.label === "Conditional Group") { item.label = ratingItem.parentRatingItemDescription; }

    // [Umbrella]
    if (item.type === "table") { return [umbrellaTable(ratingGroup, ratingItem, item)]; }

    // Recurse on child items.
    item.items = (ratingItem.relatedItems || []).reduce((arr, child) => arr.concat(arcRatingItems(child)), []);

    return contentItems ? [item].concat(contentItems) : [item];
}

function getControlType(ratingItem: any): string {
    // Get type from uiControlId.
    const controlType = ({
        0: "none",
        1: "radio",                 // "YesNoControl"
        2: "text",                  // "FreeTextControl"
        3: "number",                // "FreeNumericControl"
        4: "checkboxGroup",         // "VerticalCheckboxList"
        5: "dropdown",              // "DropDownList"
        6: "date",                  // "DateControl"
        7: "attachment",            // "Attachment"
        8: "selection",             // "Selection"
        9: "radioGroup",            // "Boolean",
        10: "aggregate",            // "Aggregate"
        11: "checkbox",             // "Checkbox"
        12: "radioGroup",           // "RadioGroup"
        13: "radio",                // "Radio"
        14: "textarea"              // "TextArea"
    })[ratingItem.uiControlId];

    // Determine table type.
    const classes = (ratingItem.className || "").split(" ");    // Get array of class names.
    if (classes.indexOf("table") > -1) { return "table"; }          // Determine table control type.

    // Determine radio group type.
    const firstChild = ratingItem.relatedItems ? ratingItem.relatedItems[0] : null;             // Get first child.
    if (controlType === "radio" && firstChild && firstChild.uiControlId === 1) {                // If type says radio and child is also radio:
        const firstGrandChild = firstChild.relatedItems ? firstChild.relatedItems[0] : null;    // Get first grandchild.
        if (firstGrandChild && firstGrandChild.uiControlId === 1) { return "radio"; }               // If grandchild is radio, this is a radio with a radioGroup child.
        return "radioGroup";                                                                    // Otherwise this is a radioGroup with a radio child.
    }

    // Return type.
    if (!controlType) { return "missed"; } // Unknown controls are handled by the caller.
    return controlType;
}

// [ATA]

function ataAggregate(group: any): any {
    let parent = group.programEnrollmentRatingItems[0];

    // Old data has no parent item in group.
    if (!parent.relatedItems.length) { parent = {
        programEnrollmentRatingItemId: group.programEnrollmentRatingGroupId,
        relatedItems: group.programEnrollmentRatingItems.map(e => (e.parentItemId = e.parentItemId || group.programEnrollmentRatingGroupId, e))
    }; }

    // Set up rules.
    const validationTarget = parent.validationTargetValue;
    const rules: any = {
        [parent.programEnrollmentRatingItemId]: {
            [parent.programEnrollmentRatingItemId + ".valid"]: { compare: [`#${parent.programEnrollmentRatingItemId}.value`, "==", validationTarget] }
        }
    };
    const reducer = (parent.stringUiControlAggregateFunction || "sum").toLowerCase() + "Reducer";

    // Set up child items.
    let items = parent.relatedItems
        .reduce((items, ratingItem) => items.concat(arcRatingItems(ratingItem)), [])
        .filter(e => e.parentItemId == parent.programEnrollmentRatingItemId);
    items.forEach(item => {
        item.type = "text";
        item.required = false;
        item.mask = "000";
        rules[item.itemId] = {
            [`${parent.programEnrollmentRatingItemId}.value`]: { reduce: [`${parent.programEnrollmentRatingItemId}.items`, reducer, 0] }
        };
    });

    // Return item.
    return {
        type: "aggregate",
        itemId: parent.programEnrollmentRatingItemId,
        parentItemId: "registerForm",
        label: `<strong>${group.ratingGroupDescription}</strong>`,
        required: true,
        hint: "Total",
        rules,
        items,
        data: parent
    };
}

// group { programEnrollmentRatingItems, programEnrollmentRatingGroupId, ratingGroupDescription }
function ataDropdown(group: any): any {
    let item = group.programEnrollmentRatingItems[0];

    // Old data has no parent item in group.
    if (!item.relatedItems.length) { item = {
        programEnrollmentRatingItemId: group.programEnrollmentRatingGroupId,
        relatedItems: group.programEnrollmentRatingItems.map(e => (e.parentItemId = e.parentItemId || group.programEnrollmentRatingGroupId, e))
    }; }

    return {
        type: "dropdown",
        itemId: item.programEnrollmentRatingItemId,
        parentItemId: "registerForm",
        label: group.ratingGroupDescription,
        placeholder: item.placeHolder,
        required: true,
        items: item.relatedItems.reduce((items, ratingItem) => items.concat(arcRatingItems(ratingItem)), []),
        data: item
    };
}

// [UMB]

// ratingGroup { programEnrollmentRatingContents }
// ratingItem { relatedItems }
// item { itemId, className }
function umbrellaTable(ratingGroup: any, ratingItem: any, item: any): any {
    // Format table rows.
    item.items = ratingItem.relatedItems.map(child => { // child { programEnrollmentRatingItemId, parentItemId, display, className  }
        const itemId = child.semanticItemId || child.programEnrollmentRatingItemId.toString();
        const parentItemId = child.semanticParentItemId || child.parentItemId.toString();

        // Add column for radio button to rating item display.
        const displayChecked = `<td><input type='radio' id='i${itemId}' name='${parentItemId}' checked></td>${child.display}`;
        const displayUnchecked = `<td><input type='radio' id='i${itemId}' name='${parentItemId}'></td>${child.display}`;

        return {
            type: "row",
            itemId,
            parentItemId,
            label: displayUnchecked,
            className: (child.className || "") + " cursor-pointer",
            valid: false,
            data: child,
            rules: {
                [parentItemId]: {
                    [`${itemId}.valid`]: { compare: [`#${parentItemId}.value`, "==", itemId] },
                    [`${itemId}.label`]: { compare: [`#${parentItemId}.value`, "==", itemId], equal: displayChecked, fallback: displayUnchecked }
                }
            }
        };
    });

    // Validate container if anything is selected.
    const rules: any = {
        [item.itemId]: {
            [`container${item.itemId}.valid`]: { compare: [`#${item.itemId}.value`, "!!"] }
        }
    };

    // Get dependencies from class name.
    if (item.className) {
        const classDependencies: any = (item.className.split(" ").filter(e => e.indexOf("dep+") > -1)[0] || "").split("+").slice(1);
        classDependencies.forEach(id => {
            if (!rules[id]) { rules[id] = {}; }
            rules[id][`container${item.itemId}.visible`] = { any: classDependencies.map(id2 => ({ compare: [`#${id2}.valid`, "==", true] })) };
        });
        item.className = item.className.replace("font-size-09em", "");
    }

    // Return item.
    return {
        type: "none",
        required: true,
        itemId: "container" + item.itemId,
        className: "font-size-09em",
        rules,
        items: [].concat(
            arcContents(ratingGroup.programEnrollmentRatingGroupContents, "Header", "container"),
            arcContents(ratingGroup.programEnrollmentRatingGroupContents, "Body", "container"),
            item,
            arcContents(ratingGroup.programEnrollmentRatingGroupContents, "Footer", "container")
        )
    };
}
